import { component } from "picoapp";
import { formatVariant, updateMaestroVariants } from "../../utilities";

const PRODUCT_GALLERY_GRID_ID = "#product-gallery-grid";
const PRODUCT_GALLERY_CAROUSEL_ID = "#product-gallery-carousel";
const variantCache = new Map();
let fetchController = new AbortController();

const stringToHTML = function (str: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return doc.body;
};

const parseNextUrl = (url: string, sectionId: string, variantId: number) => {
  const nextUrl = new URL(url);
  nextUrl.searchParams.set("variant", String(variantId));
  nextUrl.searchParams.append("sections", sectionId);

  return nextUrl;
};

const cancelActivePrefetching = () => {
  fetchController.abort();
};

const parseDom = (stringToMarkup: string) => {
  const dom = stringToHTML(stringToMarkup);
  const grid = dom.querySelector(PRODUCT_GALLERY_GRID_ID);
  const carousel = dom.querySelector(PRODUCT_GALLERY_CAROUSEL_ID);

  return {
    dom,
    grid,
    carousel,
  };
};

const getVirtualDom = async (sectionId: string, variantId: number) => {
  let partialMarkup: string;

  try {
    const nextUrl = parseNextUrl(window.location.href, sectionId, variantId);

    if (fetchController.signal.aborted) {
      fetchController = new AbortController();
    }

    const result = await fetch(nextUrl, {
      signal: fetchController.signal,
    })
      .then((res) => res.json())
      .catch((e) => console.error(e));
    partialMarkup = result?.[sectionId];
  } catch (e) {
    console.error(e);
  }

  if (!partialMarkup) {
    return;
  }

  return parseDom(partialMarkup);
};

const prefetchVariant = async (sectionId: string, variants: any[] = []) => {
  if (
    navigator &&
    // @ts-expect-error
    navigator.connection &&
    // @ts-expect-error
    (navigator.connection.effectiveType.includes("2g") ||
      // @ts-expect-error
      navigator.connection.saveData)
  ) {
    return;
  }

  const variant = variants[0];

  if (!variant) {
    return;
  }

  try {
    const xDom = await getVirtualDom(sectionId, variant.id);

    if (xDom) {
      variantCache.set(variant.id, xDom);
      variants.shift();

      if (fetchController.signal.aborted === false) {
        requestIdleCallback(() => prefetchVariant(sectionId, variants));
      }
    }
  } catch (e) {
    console.error(e);
  }
};

const createPlaceholders = () => {
  const carouselPlaceholderTemplate = document.createElement("template");

  const spinner = `
    <div role="status" class="absolute z-10 right-4 bottom-1">
      <svg class="inline mr-2 w-5 h-5 text-bone-300 animate-spin fill-bone-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
          <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
      </svg>
      <span class="sr-only">Loading...</span>
    </div>
  `;

  carouselPlaceholderTemplate.innerHTML = `
    <div id="product-gallery-carousel-placeholder" class="animate-fade-in relative">
      ${spinner}
      <div class="animate-pulse w-full aspect-[376/260] bg-bone-100"></div>
    </div>
  `;

  const gridPlaceholderTemplate = document.createElement("template");

  gridPlaceholderTemplate.innerHTML = `
    <div id="product-gallery-grid-placeholder" class="animate-fade-in relative">
      <div class="md:max-w-[764px] w-full grid grid-cols-2 gap-4">
        <div class="relative w-full aspect-[376/260] md:aspect-[764/577] col-span-2">
          ${spinner}
          <div class="animate-pulse w-full h-full box-border rounded bg-bone-100"></div>
        </div>
        ${`<div class="animate-pulse aspect-[376/260] md:aspect-square rounded box-border bg-bone-100"></div>`.repeat(
          2
        )}
      </div>
    </div>
  `;

  return {
    carouselPlaceholder: carouselPlaceholderTemplate.content.firstElementChild,
    gridPlaceholder: gridPlaceholderTemplate.content.firstElementChild,
  };
};

export const productHero = component((node: HTMLElement, context) => {
  const [, sectionId] =
    node
      .closest("[id^='shopify-section']")
      ?.getAttribute("id")
      ?.split("shopify-section-") ?? [];

  if (!sectionId) {
    return;
  }

  context.on("tbsp:variant-selected", async (variant) => {
    let xDom: ReturnType<typeof parseDom>;

    // abort current prefetches
    cancelActivePrefetching();

    // update metrics
    updateMaestroVariants(node, formatVariant(variant));

    if (variantCache.has(variant.id)) {
      xDom = variantCache.get(variant.id);
    } else {
      try {
        const existingGrid = node.querySelector(PRODUCT_GALLERY_GRID_ID);
        const existingCarousel = node.querySelector(
          PRODUCT_GALLERY_CAROUSEL_ID
        );

        const { gridPlaceholder, carouselPlaceholder } = createPlaceholders();

        existingGrid.replaceWith(gridPlaceholder);
        existingCarousel.replaceWith(carouselPlaceholder);

        xDom = await getVirtualDom(sectionId, variant.id);
        variantCache.set(variant.id, xDom);
      } catch (e) {
        console.error(e);
      }
    }

    if (!xDom) {
      return window.location.reload();
    }

    const existingGridOrPlaceholder = node.querySelector(
      `[id^="${PRODUCT_GALLERY_GRID_ID.substring(1)}"]`
    );
    const existingCarouselOrPlaceholder = node.querySelector(
      `[id^="${PRODUCT_GALLERY_CAROUSEL_ID.substring(1)}"]`
    );

    if (existingGridOrPlaceholder) {
      existingGridOrPlaceholder.replaceWith(xDom.grid.cloneNode(true));
    }

    if (existingCarouselOrPlaceholder) {
      existingCarouselOrPlaceholder.replaceWith(xDom.carousel.cloneNode(true));
    }

    window.document.dispatchEvent(
      new Event("shopify:section:load", {
        bubbles: true,
        cancelable: true,
      })
    );
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          requestIdleCallback(() => {
            const {
              product: { variants },
            } = context.getState();

            const targetVariants = [...variants];

            Array.from({ length: 2 }).forEach(() => {
              prefetchVariant(sectionId, targetVariants);
            });
          });

          observer.disconnect();
        }
      });
    },
    { rootMargin: `0px` }
  );

  observer.observe(node);

  return (node: HTMLButtonElement) => {
    observer.disconnect();
  };
});
